import React, { useState, useEffect, useRef } from 'react';
import SquareCardForm from '../SquareCardForm';
import styles from './PurchaseCredits.module.css';

const TOKEN_KEY = 'ngs_parent_token';
function authHeader() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmt(n) { return `$${Number(n || 0).toFixed(2)}`; }

export default function PurchaseCredits({ onPurchased }) {
  const [data, setData] = useState(null);
  const [dataError, setDataError] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/parent/credit-packs', { headers: authHeader() });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed to load credit packs');
        setData(json);
        if (json.creditPackages?.[0]) setPackageId(json.creditPackages[0].id);
      } catch (e) {
        setDataError(e.message);
      }
    })();
  }, []);

  if (dataError) return <div className={styles.errorCard}>Couldn't load credit packs: {dataError}</div>;
  if (!data) return <div className={styles.loadingCard}>Loading credit packages…</div>;

  const selectedPkg = (data.creditPackages || []).find(p => p.id === packageId);
  const customCents = Math.round(parseFloat(customAmount || 0) * 100);
  const activeAmountCents = useCustom ? customCents : Math.round((selectedPkg?.amount || 0) * 100);
  const canSubmit = !submitting && activeAmountCents >= 100;

  // Confirmation screen
  if (result) {
    return (
      <div className={styles.confirmCard}>
        <div className={styles.confirmCheck}>✓</div>
        <div className={styles.confirmTitle}>Credits Added</div>
        <div className={styles.confirmSub}>A receipt has been emailed to {data.client.email}.</div>
        <div className={styles.confirmDetails}>
          <div className={styles.detailRow}><span>Package</span><strong>{result.packageLabel}</strong></div>
          <div className={styles.detailRow}><span>Amount charged</span><strong>{fmt(result.transaction.amount)}</strong></div>
          <div className={styles.detailRow}><span>New balance</span><strong>{fmt(result.newBalance)}</strong></div>
        </div>
        {result.payment?.receiptUrl && (
          <a href={result.payment.receiptUrl} target="_blank" rel="noreferrer" className={styles.receiptLink}>View Square Receipt →</a>
        )}
        <button className={styles.primaryBtn} onClick={() => {
          setResult(null);
          setCustomAmount('');
          setUseCustom(false);
        }}>Purchase More Credits</button>
      </div>
    );
  }

  const submit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const sourceId = await cardRef.current.tokenize();
      const body = useCustom
        ? { customAmountCents: customCents, sourceId }
        : { packageId, sourceId };
      const res = await fetch('/api/parent/credit-packs/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Purchase failed');
      setResult(json);
      onPurchased?.();
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <h2 className={styles.title}>Purchase Credits</h2>
      <div className={styles.balanceStrip}>
        <span className={styles.balanceLabel}>Current balance</span>
        <span className={styles.balanceValue}>{fmt(data.client.balance)}</span>
      </div>

      <div className={styles.sectionLabel}>Choose a Package</div>
      <div className={styles.packGrid}>
        {(data.creditPackages || []).map(p => {
          const isActive = !useCustom && p.id === packageId;
          return (
            <button
              key={p.id}
              className={`${styles.packCard} ${isActive ? styles.packCardActive : ''}`}
              onClick={() => { setPackageId(p.id); setUseCustom(false); }}
            >
              <div className={styles.packLabel}>{p.label}</div>
              <div className={styles.packAmount}>{fmt(p.amount)}</div>
              {p.description && <div className={styles.packDesc}>{p.description}</div>}
            </button>
          );
        })}
        <button
          className={`${styles.packCard} ${useCustom ? styles.packCardActive : ''}`}
          onClick={() => setUseCustom(true)}
        >
          <div className={styles.packLabel}>Custom</div>
          <div className={styles.packAmount}>$—</div>
          <div className={styles.packDesc}>Pick your own amount</div>
        </button>
      </div>

      {useCustom && (
        <div className={styles.fieldBlock}>
          <label className={styles.fieldLabel}>Custom Amount (USD)</label>
          <input
            type="number"
            step="0.01"
            min="1"
            className={styles.input}
            value={customAmount}
            onChange={e => setCustomAmount(e.target.value)}
            placeholder="e.g. 125.00"
          />
        </div>
      )}

      <div className={styles.sectionLabel} style={{ marginTop: 18 }}>Payment</div>
      <div className={styles.payNote}>Secure payment via Square — your card details never touch NGS servers.</div>
      <div className={styles.payBox}>
        <SquareCardForm ref={cardRef} config={data.payments} />
      </div>

      {submitError && <div className={styles.errorInline}>{submitError}</div>}

      <button
        className={styles.primaryBtn}
        onClick={submit}
        disabled={!canSubmit}
        style={{ marginTop: 18 }}
      >
        {submitting ? 'Processing…' : `Purchase ${fmt(activeAmountCents / 100)}`}
      </button>
    </div>
  );
}
